import React, { useState, useEffect } from 'react';
import DOMPurify from "dompurify";

// Material UI
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';

// Material UI Icons
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import SaveIcon from '@mui/icons-material/Save';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

// Componentes 
import SeleccionarRubro from './SeleccionarRubro';
import SeleccionarEspecialidades from './SeleccionarEspecialidades';
import SeleccionarPais from './SeleccionarPais';
import SeleccionarProvincia from './SeleccionarProvincia';
import SeleccionarLocalidad from './SeleccionarLocalidad';

// Quill components
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // Estilo para el editor Quill

// Funciones para obtener datos
import ObtenerAgenteComercial from '../functions/ObtenerAgenteComercial';
import InsertarAgenteComercial from '../functions/InsertarAgenteComercial';
import GuardarAgenteComercial from '../functions/GuardarAgenteComercial';

// Tipos de datos
import ComercialTabla from '../types/ComercialTabla';
import PaisSeleccionar from '../types/PaisSeleccionar';
import ProvinciaSeleccionar from '../types/ProvinciaSeleccionar';
import LocalidadSeleccionar from '../types/LocalidadSeleccionar';

// Indicador de procesamiento
import CircularProgress from '@mui/material/CircularProgress';

interface EditarAgenteComercialProps {
    idAgenteComercial: number;
    guardarCambios: (agentecomercial: ComercialTabla) => void;
    cancelarCambios: () => void;
    eliminando: boolean;
}
  
export const EditarAgenteComercial: React.FC<EditarAgenteComercialProps> = ({ idAgenteComercial, guardarCambios, cancelarCambios, eliminando = false }) => {
  
    const [isLoading, setIsLoading] = useState(true);
    const [agenteComercial, setAgenteComercial] = useState<ComercialTabla | null>(null);
    
    const [errorAgenteComercial, setErrorAgenteComercial] = useState<string | null>(null);

    const [localCuit, setLocalCuit] = useState('');
    const [localCodigo, setLocalCodigo] = useState('');
    const [localNombreFantasia, setLocalNombreFantasia] = useState('');
    const [localApellido, setLocalApellido] = useState('');
    const [localNombres, setLocalNombres] = useState('');
    const [localDomicilio, setLocalDomicilio] = useState('');
    const [localTelefono, setLocalTelefono] = useState('');
    const [localCelular, setLocalCelular] = useState('');
    const [localEmail, setLocalEmail] = useState('');
    const [localObservaciones, setLocalObservaciones] = useState('');

    const [localHabilitado, setLocalHabilitado] = useState('');
    const [localFchCrea, setLocalFchCrea] = useState('');
    const [localFchModi, setLocalFchModi] = useState('');
    const [localDadoBaja, setLocalDadoBaja] = useState('');
    const [localFchBaja, setLocalFchBaja] = useState('');

    const [localIdPais, setLocalIdPais] = useState(0);
    const [localPais, setLocalPais] = useState('');
    const [localIdProvincia, setLocalIdProvincia] = useState(0);
    const [localProvincia, setLocalProvincia] = useState('');  
    const [localIdLocalidad, setLocalIdLocalidad] = useState(0);
    const [localLocalidad, setLocalLocalidad] = useState('');
    const [localCodPostal, setLocalCodPostal] = useState('');
    
    useEffect(() => {
        
        const fetchAgenteComercial = async () => {
            try {

                const data = await ObtenerAgenteComercial("",idAgenteComercial);

                if (data.agentecomercial_ok === 'si') {

                    // Todo ok, prestador recuperado
                    const agenteComercialRecuperado: ComercialTabla = {
                        id: data.agentecomercial[0].id,
                        codigo: data.agentecomercial[0].codigo,
                        cuit: data.agentecomercial[0].cuit,
                        apellido: data.agentecomercial[0].apellido,
                        nombres: data.agentecomercial[0].nombres,
                        nombrefantasia: data.agentecomercial[0].nombrefan,
                        domicilio: data.agentecomercial[0].domicilio,
                        telefono: data.agentecomercial[0].telefono,
                        celular: data.agentecomercial[0].celular,
                        email: data.agentecomercial[0].email,
                        habilitado: data.agentecomercial[0].habilitado,
                        fecha_creacion: data.agentecomercial[0].fecha_creacion,
                        fecha_modificacion: data.agentecomercial[0].fecha_modificacion,
                        dado_de_baja: data.agentecomercial[0].dadobaja,
                        fecha_baja: data.agentecomercial[0].fecha_baja,
                        idpais: data.agentecomercial[0].idpais,
                        pais: data.agentecomercial[0].pais,
                        idprovincia: data.agentecomercial[0].idprovincia,
                        provincia: data.agentecomercial[0].provincia,
                        idlocalidad: data.agentecomercial[0].idlocalidad,
                        localidad: data.agentecomercial[0].localidad,
                        codpostal: data.agentecomercial[0].codpostal,
                        observaciones: data.agentecomercial[0].observaciones
                    };

                    setAgenteComercial(agenteComercialRecuperado);

                    setLocalCodigo(data.agentecomercial[0].codigo);
                    setLocalCuit(data.agentecomercial[0].cuit);
                    setLocalApellido(data.agentecomercial[0].apellido);
                    setLocalNombres(data.agentecomercial[0].nombres);
                    setLocalNombreFantasia(data.agentecomercial[0].nombrefan);
                    setLocalDomicilio(data.agentecomercial[0].domicilio);
                    setLocalTelefono(data.agentecomercial[0].telefono);
                    setLocalCelular(data.agentecomercial[0].celular);
                    setLocalEmail(data.agentecomercial[0].email);
                    setLocalObservaciones(data.agentecomercial[0].observaciones);
                    setLocalHabilitado(data.agentecomercial[0].habilitado);
                    setLocalFchCrea(data.agentecomercial[0].fecha_creacion);
                    setLocalFchModi(data.agentecomercial[0].fecha_modificacion);
                    setLocalDadoBaja(data.agentecomercial[0].dadobaja);
                    setLocalFchBaja(data.agentecomercial[0].fecha_baja);
                    setLocalIdPais(data.agentecomercial[0].idpais);
                    setLocalPais(data.agentecomercial[0].pais);
                    setLocalIdProvincia(data.agentecomercial[0].idprovincia);
                    setLocalProvincia(data.agentecomercial[0].provincia);
                    setLocalIdLocalidad(data.agentecomercial[0].idlocalidad);
                    setLocalLocalidad(data.agentecomercial[0].localidad);
                    setLocalCodPostal(data.agentecomercial[0].codpostal);

                    setErrorAgenteComercial(null);
                } else {
                    
                    // Hubo errores al recuperar la pregunta frecuente
                    setErrorAgenteComercial(data.mensaje);
                    setAgenteComercial(null);
                    setLocalCodigo('');
                    setLocalCuit('');
                    setLocalApellido('');
                    setLocalNombres('');
                    setLocalNombreFantasia('');
                    setLocalDomicilio('');
                    setLocalTelefono('');
                    setLocalCelular('');
                    setLocalEmail('');
                    setLocalObservaciones('');
                    setLocalHabilitado('');
                    setLocalFchCrea('');
                    setLocalFchModi('');
                    setLocalDadoBaja('');
                    setLocalFchBaja('');
                    setLocalIdPais(0);
                    setLocalPais('');
                    setLocalIdProvincia(0);
                    setLocalProvincia('');
                    setLocalIdLocalidad(0);
                    setLocalLocalidad('');
                    setLocalCodPostal('');
                }

                setIsLoading(false);
                                
            } catch (err) {
                setErrorAgenteComercial('Error al obtener la información del agente comercial.');
                setAgenteComercial(null);
                setLocalCodigo('');
                setLocalCuit('');
                setLocalApellido('');
                setLocalNombres('');
                setLocalNombreFantasia('');
                setLocalDomicilio('');
                setLocalTelefono('');
                setLocalCelular('');
                setLocalEmail('');
                setLocalObservaciones('');
                setLocalHabilitado('');
                setLocalFchCrea('');
                setLocalFchModi('');
                setLocalDadoBaja('');
                setLocalFchBaja('');
                setLocalIdPais(0);
                setLocalPais('');
                setLocalIdProvincia(0);
                setLocalProvincia('');
                setLocalIdLocalidad(0);
                setLocalLocalidad('');
                setLocalCodPostal('');

                setIsLoading(false);
            }
        };

        if (idAgenteComercial > 0) {
            // Estoy editando un agente comercial
            fetchAgenteComercial();
        } else {
            // Estoy creando un nuevo agente comercial
            setIsLoading(false);
            setAgenteComercial({id:0, codigo:'', cuit:'', apellido:'', nombres:'', nombrefantasia:'', domicilio:'', telefono:'', celular:'', email:'', habilitado:'', fecha_creacion:'', fecha_modificacion:'', dado_de_baja:'', fecha_baja:'', idpais:0, pais:'', idprovincia:0, provincia:'', idlocalidad:0, localidad:'', codpostal:'', observaciones:''});
            setLocalCodigo('');
            setLocalCuit('');
            setLocalApellido('');
            setLocalNombres('');
            setLocalNombreFantasia('');
            setLocalDomicilio('');
            setLocalTelefono('');
            setLocalCelular('');
            setLocalEmail('');
            setLocalObservaciones('');
            setLocalFchCrea('');
            setLocalFchModi('');
            setLocalFchBaja('');
            setLocalIdPais(0);
            setLocalPais('');
            setLocalIdProvincia(0);
            setLocalProvincia('');
            setLocalIdLocalidad(0);
            setLocalLocalidad('');
            setLocalCodPostal('');
            setLocalHabilitado('S');
            setLocalDadoBaja('N');
        }

    }, []);

    const handleChangeLocalCuit = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^[0-9]{0,11}$/;
        if (pattern.test(event.target.value)) {
            setLocalCuit(event.target.value);
        }
    };

    const handleChangeLocalNombreFantasia = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalNombreFantasia(event.target.value.toUpperCase());
    };

    const handleChangeLocalCodigo = (event: React.ChangeEvent<HTMLInputElement>) => {
        //const pattern = /^[0-9]{0,11}$/;
        //if (pattern.test(event.target.value)) {
            if (event.target.value.length <= 8) {
                setLocalCodigo(event.target.value.toUpperCase());
            }
        //}
    };

    const handleChangeLocalApellido = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalApellido(event.target.value.toUpperCase());
    };

    const handleChangeLocalNombres = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalNombres(event.target.value.toUpperCase());
    };

    const handleChangeLocalDomicilio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalDomicilio(event.target.value.toUpperCase());
    };

    const handleChangeLocalTelefono = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^[0-9]{0,15}$/;
        if (pattern.test(event.target.value)) {
            setLocalTelefono(event.target.value);
        }
    };

    const handleChangeLocalCelular = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^[0-9]{0,10}$/;
        if (pattern.test(event.target.value)) {
            setLocalCelular(event.target.value);
        }
    };

    const handleChangeLocalEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalEmail(event.target.value.toUpperCase());
    };

    const handleChangeLocalObservaciones = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalObservaciones(event.target.value);
    };

    const handleChangeLocalHabitado = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalHabilitado(valorMayuscula);
    };

    const handleChangeLocalDadoBaja = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalDadoBaja(valorMayuscula);
    };

    const handlePaisChange = (paisSeleccionado: PaisSeleccionar) => {
        setLocalIdPais(paisSeleccionado.id);
    }

    const handlePaisClear = () => {
        setLocalIdPais(0);
    }

    const handleProvinciaChange = (provinciaSeleccionada: ProvinciaSeleccionar) => {
        setLocalIdProvincia(provinciaSeleccionada.id);
    }

    const handleProvinciaClear = () => {
        setLocalIdProvincia(0);
    }

    const handleLocalidadChange = (localidadSeleccionada: LocalidadSeleccionar) => {
        setLocalIdLocalidad(localidadSeleccionada.id);
    }

    const handleLocalidadClear = () => {
        setLocalIdLocalidad(0);
    }

    
    const guardarCambiosLocal = async () => {

        if (idAgenteComercial === 0) {
            // Crear nuevo agente comercial

            const AgenteComercialNuevo: ComercialTabla = {
                id: 0,
                codigo: localCodigo,
                cuit: localCuit,
                apellido: localApellido,
                nombres: localNombres,
                nombrefantasia: localNombreFantasia,
                domicilio: localDomicilio,
                telefono: localTelefono,
                celular: localCelular,
                email: localEmail,
                habilitado: localHabilitado,
                fecha_creacion: '',
                fecha_modificacion: '',
                dado_de_baja: 'N',
                fecha_baja: '',
                idpais: localIdPais,
                pais: localPais,
                idprovincia: localIdProvincia,
                provincia: localProvincia,
                idlocalidad: localIdLocalidad,
                localidad: localLocalidad,
                codpostal: localCodPostal,
                observaciones: localObservaciones
            };
            
            try {
                const agenteInsertado = await InsertarAgenteComercial(AgenteComercialNuevo);

                if (agenteInsertado.comercial_insertado === 'si') {
                    guardarCambios(AgenteComercialNuevo);
                } else {
                    alert('Error al guardar los cambios: ' + agenteInsertado.mensaje);
                }
            } catch (error) {
                alert('Error al guardar los cambios: ' + error);
            }
        } else {

            if (eliminando) {
                
                // Se realiza la baja lógica del agente comercial
                // HABILITADO = N
                // DADO_BAJA = S
                const fechaActual = new Date().toISOString();
                const guardarFecha = fechaActual.slice(0, 10)+" "+fechaActual.slice(11, 19);

                // Modificar datos agente comercial
                const AgenteComercialEditado: ComercialTabla = {
                    id: idAgenteComercial,
                    codigo: localCodigo,
                    cuit: localCuit,
                    apellido: localApellido,
                    nombres: localNombres,
                    nombrefantasia: localNombreFantasia,
                    domicilio: localDomicilio,
                    telefono: localTelefono,
                    celular: localCelular,
                    email: localEmail,
                    habilitado: 'N',
                    fecha_creacion: localFchCrea,
                    fecha_modificacion: guardarFecha,
                    dado_de_baja: 'S',
                    fecha_baja: guardarFecha,
                    idpais: localIdPais,
                    pais: localPais,
                    idprovincia: localIdProvincia,
                    provincia: localProvincia,
                    idlocalidad: localIdLocalidad,
                    localidad: localLocalidad,
                    codpostal: localCodPostal,
                    observaciones: localObservaciones
                };

                const respuesta = await GuardarAgenteComercial(AgenteComercialEditado);

                if (respuesta.agentecomercial_guardado === 'si') {
                    guardarCambios(AgenteComercialEditado);
                } else {
                    alert('Error al guardar los cambios: ' + respuesta.mensaje);
                }
                
            } else {
                
                // Modificar agente comercial
                const fechaActual = new Date().toISOString();
                const guardarFecha = fechaActual.slice(0, 10)+" "+fechaActual.slice(11, 19);
                
                // Modificar datos pregunta frecuente
                const AgenteComercialEditado: ComercialTabla = {
                    id: idAgenteComercial,
                    cuit: localCuit,
                    nombrefantasia: localNombreFantasia,
                    codigo: localCodigo,
                    apellido: localApellido,
                    nombres: localNombres,
                    domicilio: localDomicilio,
                    telefono: localTelefono,
                    celular: localCelular,
                    email: localEmail,
                    habilitado: localHabilitado,
                    fecha_creacion: localFchCrea,
                    fecha_modificacion: guardarFecha,
                    dado_de_baja: localDadoBaja,
                    fecha_baja: localDadoBaja === 'S' ? guardarFecha : '',
                    idpais: localIdPais,
                    pais: localPais,
                    idprovincia: localIdProvincia,
                    provincia: localProvincia,
                    idlocalidad: localIdLocalidad,
                    localidad: localLocalidad,
                    codpostal: localCodPostal,
                    observaciones: localObservaciones
                };

                const respuesta = await GuardarAgenteComercial(AgenteComercialEditado);

                if (respuesta.agentecomercial_guardado === 'si') {
                    guardarCambios(AgenteComercialEditado);
                } else {
                    alert('Error al guardar los cambios: ' + respuesta.mensaje);
                }
            }
        }
    };

    return (
        <>
            {isLoading && (
                <div>
                    <p className="text-lg">Por favor espere</p>
                    <CircularProgress />
                </div>
            )}
            {(!isLoading && agenteComercial) && (
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-5">
                    {eliminando ? (
                        <h1 className="font-metropolis text-xl text-red-700 font-bold mb-2">Eliminando Agente Comercial: {agenteComercial?.apellido}, {agenteComercial?.nombres}</h1>
                    ) : (
                        <h1 className="font-metropolis text-xl font-bold mb-2">{idAgenteComercial > 0 ? "Editando Agente Comercial: " + agenteComercial?.apellido + ', ' + agenteComercial?.nombres : "Nuevo Agente Comercial"}</h1>
                    )}
                    {/* Campos principales */}     
                    <div className="border-t-2 border-vp-azul w-full flex flex-row pt-3 justify-between items-start lg:space-x-2 xxs:flex-wrap xs:flex-wrap sm:flex-wrap md:flex-wrap lg:flex-wrap xl:flex-nowrap xxl:flex-nowrap mb-2">
                        <div className="flex flex-col justify-start items-center max-w-[120px] flex-grow w-full">
                            <TextField id="cuit" label="Cuit" variant="outlined" value={localCuit} autoFocus
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalCuit}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[380px] flex-grow w-full">
                            <TextField id="nombrefantasia" label="Nombre Fantasía" variant="outlined" value={localNombreFantasia}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalNombreFantasia}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center flex-grow w-full max-w-[120px]">
                            <TextField id="codigo" label="Código" variant="outlined" value={localCodigo}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalCodigo}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[380px] flex-grow w-full">
                            <TextField id="apellido" label="Apellido" variant="outlined" value={localApellido}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalApellido}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[380px] flex-grow w-full">
                            <TextField id="nombres" label="Nombres" variant="outlined" value={localNombres}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalNombres}
                                disabled={eliminando}
                            />
                        </div>
                    </div>
                    {/* Campos de contacto */}
                    <div className="flex flex-row justify-between items-start lg:space-x-2 xxs:flex-wrap xs:flex-wrap sm:flex-wrap md:flex-wrap lg:flex-wrap xl:flex-nowrap xxl:flex-nowrap mb-2">
                        <div className="flex flex-col justify-start items-center max-w-[380px] flex-grow w-full">
                            <TextField id="domicilio" label="Domicilio" variant="outlined" value={localDomicilio}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalDomicilio}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[160px] flex-grow w-full">
                            <TextField id="telefono" label="Teléfono" variant="outlined" value={localTelefono}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalTelefono}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[160px] flex-grow w-full">
                            <TextField id="celular" label="Celular" variant="outlined" value={localCelular}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalCelular}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[380px] flex-grow w-full">
                            <TextField id="email" label="Email" variant="outlined" value={localEmail}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalEmail}
                                disabled={eliminando}
                            />
                        </div>
                    </div>
                    {/* Observaciones */}
                    <div className="flex flex-row justify-between items-start lg:space-x-2 xxs:flex-wrap xs:flex-wrap sm:flex-wrap md:flex-wrap lg:flex-wrap xl:flex-nowrap xxl:flex-nowrap mb-2">
                        <div className="flex flex-col justify-start items-center flex-grow w-full">
                            <TextField id="observaciones" label="Observaciones" variant="outlined" rows={3} value={localObservaciones}
                                sx={{
                                '& .MuiOutlinedInput-input': {
                                    padding: '8px',
                                },
                                '& .MuiInputLabel-outlined': {
                                    top: '-6px',
                                },
                                '& .MuiInputLabel-shrink': {
                                    top: '0px',
                                },
                                width: '100%',
                                marginRight: '6px',
                                marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalObservaciones} disabled={eliminando}
                            />
                        </div>
                    </div>
                    {/* Fechas y habilitado */}
                    <div className="flex flex-row justify-between items-start space-x-2 border-b-2 border-vp-azul pb-3">
                        <div className="flex flex-col justify-start items-center flex-grow w-full mt-2">
                            <div className="flex flex-row flex-wrap justify-between items-center flex-grow w-full">
                                {agenteComercial.id > 0 && (<>
                                    <div className="flex flex-col justify-between items-center max-w-[170px] flex-grow w-full">
                                        <TextField id="fchcrea" label="Fec. Creado" variant="outlined" value={localFchCrea}
                                            sx={{
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '8px',
                                                },
                                                '& .MuiInputLabel-outlined': {
                                                    top: '-6px',
                                                },
                                                '& .MuiInputLabel-shrink': {
                                                    top: '0px',
                                                },
                                                width: '100%',
                                                marginRight: '6px',
                                                marginBottom: '8px',
                                            }}
                                            disabled
                                        />
                                    </div>
                                    <div className="flex flex-col justify-between items-center max-w-[170px] flex-grow w-full">
                                    <TextField id="fchcmodi" label="Fec. Modificado" variant="outlined" value={localFchModi}
                                        sx={{
                                            '& .MuiOutlinedInput-input': {
                                                padding: '8px',
                                            },
                                            '& .MuiInputLabel-outlined': {
                                                top: '-6px',
                                            },
                                            '& .MuiInputLabel-shrink': {
                                                top: '0px',
                                            },
                                            width: '100%',
                                            marginRight: '6px',
                                            marginBottom: '8px',
                                        }}
                                        disabled
                                    />
                                </div>                                    
                                </>)
                                }

                                <div className="flex flex-col justify-between items-center max-w-[100px] flex-grow w-full">
                                    <FormControl fullWidth sx={{marginRight: '6px',}}>
                                        <InputLabel id="label-habilitado"
                                            sx={{top: '-6px',
                                                '&.MuiInputLabel-shrink': {
                                                    transform: 'translate(14px, 0px) scale(0.75)',
                                                },
                                            }}
                                            >
                                                Habilitado
                                            </InputLabel>
                                        <Select
                                            labelId="label-habilitado"
                                            id="habilitado"
                                            value={localHabilitado}
                                            label="Habilitado"
                                            sx={{
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '8px',
                                                },
                                                width: '100%',
                                                marginRight: '6px',
                                                marginBottom: '8px',
                                            }}
                                            onChange={handleChangeLocalHabitado}
                                            disabled={eliminando}
                                            >
                                            <MenuItem value={'S'}>Si</MenuItem>
                                            <MenuItem value={'N'}>No</MenuItem>
                                        </Select>
                                    </FormControl> 
                                </div>
                                <div className="flex flex-col justify-between items-center max-w-[100px] flex-grow w-full">
                                    <FormControl fullWidth sx={{marginRight: '6px',}}>
                                        <InputLabel id="label-dadobaja"
                                            sx={{top: '-6px',
                                                '&.MuiInputLabel-shrink': {
                                                    transform: 'translate(14px, 0px) scale(0.75)',
                                                },
                                            }}
                                            >
                                                Baja
                                            </InputLabel>
                                        <Select
                                            labelId="label-dadobaja"
                                            id="dadobaja"
                                            value={localDadoBaja}
                                            label="Baja"
                                            sx={{
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '8px',
                                                },
                                                width: '100%',
                                                marginRight: '6px',
                                                marginBottom: '8px',
                                            }}
                                            onChange={handleChangeLocalDadoBaja}
                                            disabled={eliminando}
                                            >
                                            <MenuItem value={'S'}>Si</MenuItem>
                                            <MenuItem value={'N'}>No</MenuItem>
                                        </Select>
                                    </FormControl> 
                                </div>
                                {localDadoBaja === 'S' && (
                                    <div className="flex flex-col justify-between items-center max-w-[170px] flex-grow w-full">
                                        <TextField id="fchbaja" label="Fec. Baja" variant="outlined" value={localFchBaja}
                                            sx={{
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '8px',
                                                },
                                                '& .MuiInputLabel-outlined': {
                                                    top: '-6px',
                                                },
                                                '& .MuiInputLabel-shrink': {
                                                    top: '0px',
                                                },
                                                width: '100%',
                                                marginRight: '6px',
                                                marginBottom: '8px',
                                            }}
                                            disabled
                                        />
                                    </div>)
                                }
                                <div className="flex flex-col justify-between items-center flex-grow w-full mb-2">
                                    <SeleccionarPais paisInicial={localIdPais} onChange={handlePaisChange} onClear={handlePaisClear} eliminando={eliminando}/>
                                </div>
                                <div className="flex flex-col justify-between items-center flex-grow w-full mb-2">
                                    <SeleccionarProvincia paisInicial={localIdPais} provinciaInicial={localIdProvincia} onChange={handleProvinciaChange} onClear={handleProvinciaClear} eliminando={eliminando}/>
                                </div>
                                <div className="flex flex-col justify-between items-center flex-grow w-full mb-2">
                                    <SeleccionarLocalidad paisInicial={localIdPais} provinciaInicial={localIdProvincia} localidadInicial={localIdLocalidad} onChange={handleLocalidadChange} onClear={handleLocalidadClear} eliminando={eliminando}/>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-row justify-end items-center space-x-2">
                        {eliminando ? (
                            <>
                                <button className="mt-3 vp-btn-rojo-sm" onClick={guardarCambiosLocal} ><DeleteForeverIcon /> Eliminar</button>
                                <button className="mt-3 vp-btn-azul-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                            </>
                        ) : (
                            <>
                                <button className="mt-3 vp-btn-azul-sm" onClick={guardarCambiosLocal} ><SaveIcon /> Guardar Cambios</button>
                                <button className="mt-3 vp-btn-rojo-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                            </>
                        )}
                        
                    </div>
                </div>
            )}
        </>
    );
};
